import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  FirestoreDataConverter,
  Unsubscribe,
} from 'firebase/firestore';
import type { BigNumber } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { bigNumberConverter as clientBigNumberConverter } from '../converters/bigNumberConverter';

/**
 * BigNumberService
 *
 * Type-safe service for BigNumber entity CRUD operations.
 * BigNumbers are stored as subcollection under projects.
 *
 * Path: projects/{projectId}/bignumbers/{bigNumberId}
 *
 * Features:
 * - Basic CRUD operations (create, read, delete)
 * - Type-safe: only BigNumber types exposed, no Firebase types in public API
 * - Uses bigNumberConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { BigNumberService } from '@/modules/firebase-core/services/BigNumberService';
 *
 * const service = new BigNumberService('project-id');
 *
 * // Create
 * const bigNumber = await service.create({
 *   description: 'Monthly active users',
 *   amount: '1M+'
 * });
 *
 * // Read
 * const found = await service.get(bigNumber.id!);
 *
 * // Delete
 * await service.delete(bigNumber.id!);
 * ```
 */
export class BigNumberService {
  private readonly collectionName = 'bignumbers';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<BigNumber>;
  private readonly projectId: string;

  /**
   * Create a new BigNumberService instance
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for BigNumber entity (defaults to client SDK converter)
   */
  constructor(projectId: string, converter: FirestoreDataConverter<BigNumber> = clientBigNumberConverter) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Create a new big number
   *
   * @param data - Partial big number data (id will be auto-generated if not provided)
   * @returns {Promise<BigNumber>} The created big number with generated ID
   *
   * @example
   * ```typescript
   * const bigNumber = await service.create({
   *   description: 'Total downloads',
   *   amount: '500K'
   * });
   * ```
   */
  async create(data: Partial<BigNumber>): Promise<BigNumber> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName);
      const docRef = doc(collectionRef).withConverter(this.converter);

      const bigNumberData: BigNumber = {
        ...data,
        id: docRef.id,
        createdAt: new Date(),
      } as BigNumber;

      await setDoc(docRef, bigNumberData);

      return bigNumberData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BigNumberService.create');
      throw error;
    }
  }

  /**
   * Get a big number by ID
   *
   * @param id - BigNumber ID
   * @returns {Promise<BigNumber | null>} The big number or null if not found
   *
   * @example
   * ```typescript
   * const bigNumber = await service.get('bignumber-123');
   * if (bigNumber) {
   *   console.log(bigNumber.amount);
   * }
   * ```
   */
  async get(id: string): Promise<BigNumber | null> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id)
        .withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as BigNumber;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BigNumberService.get');
      throw error;
    }
  }

  /**
   * Delete a big number
   *
   * @param id - BigNumber ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('bignumber-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BigNumberService.delete');
      throw error;
    }
  }

  /**
   * Update a big number
   *
   * @param id - BigNumber ID
   * @param data - Partial big number data to merge
   * @returns {Promise<void>}
   */
  async update(id: string, data: Partial<BigNumber>): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await updateDoc(docRef, data as Record<string, unknown>);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BigNumberService.update');
      throw error;
    }
  }

  /**
   * Subscribe to real-time big number updates
   *
   * @param onData - Callback with updated big numbers array
   * @param onError - Optional error callback
   * @returns {Unsubscribe} Unsubscribe function
   */
  subscribe(
    onData: (items: BigNumber[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
      .withConverter(this.converter);
    const q = query(collectionRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as BigNumber)));
    }, onError);
  }

  /**
   * List big numbers for the project
   *
   * @param options - Query options
   * @param options.limit - Maximum number of big numbers to return (default: 20)
   * @returns {Promise<BigNumber[]>} Array of big numbers
   *
   * @example
   * ```typescript
   * const bigNumbers = await service.list({ limit: 50 });
   * ```
   */
  async list(options: { limit?: number } = {}): Promise<BigNumber[]> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
        .withConverter(this.converter);

      const pageLimit = options.limit || 20;
      const q = query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as BigNumber));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BigNumberService.list');
      throw error;
    }
  }
}
